import angular from "angular";

import { Calculation, Calculations, CalculationStatus, CalculatorLogic, PaymentChannel, Translations, CalculationListItem, SystemRole } from "@salaxy/core";

import { InvoicesService, ReportsService, SessionService, UiHelpers } from "../../services";
import { ApiCrudObjectController } from "../bases";

/**
 * The new ApiCrudObject type of CRUD controller for the calculations.
 */
export class CalculationCrudController extends ApiCrudObjectController<Calculation, CalculationListItem> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Calculations", "ReportsService", "UiHelpers", "$location", "$routeParams", "SessionService", "InvoicesService"];

  /**
   * If true, will also update the calculation rows from employment relation when the employment
   * relation is selected. Note that this value is true by default for historical reasons.
   */
  public updateRows: boolean;

  constructor(
    private calculationsApi: Calculations,
    protected reportsService: ReportsService,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    protected sessionService: SessionService,
    protected invoicesService: InvoicesService,
  ) {
    // Dependency injection
    super(calculationsApi, uiHelpers, $location, $routeParams);
  }
  /**
   * Initialization code.
   */
  public $onInit() {
    super.$onInit();
    if (this.updateRows == null) {
      this.updateRows = true;
    }
  }

  /** Implements the getDefaults of ApiCrudObjectController */
  public getDefaults() {
    return {
      listUrl: this.listUrl || "/calc",
      detailsUrl: this.detailsUrl || "/calc/details/",
      oDataTemplateUrl: "salaxy-components/odata/lists/CalculationsAll.html",
      oDataOptions: {},
    };
  }

  /** Recalculates the current calculation */
  public recalculate() {
    this.setStatus(this.calculationsApi.recalculate(this.current))
      .then((calc) => {
        this.setCurrentValue(calc);
      });
  }

  /**
   * Gets the URL for a calculation pdf.
   * If report has not been saved (ID is null), returns null.s
   * @param calc - Calculation. This method requires that the calculation has been saved.
   * @returns Url for specified report
   */
  public getPdfUrl(calc: Calculation) {
    if (!calc) {
      return null;
    }
    return this.reportsService.getPdfUrlForCalc("salarySlip", calc.id, true);
  }

  /**
   * If true, this is a new unsaved object.
   * Adds "new-for-employment" actions as new item.
   */
  public isNew(): boolean {
    if (this.parentController) {
      return this.parentController.isNew();
    }
    return this.action === "new-for-worker" || this.action === "new-for-employment" || super.isNew();
  }

  /**
   * Starts the reload process depending on the bindingMode:
   * Overrides a different load process for "new-for-employment".
   */
  public reload(): Promise<Calculation> {
    if (this.action === "new-for-worker" || this.action === "new-for-employment") {
      if (this.parentController) {
        return this.parentController.reload().then((item) => {
          this._currentId = null;
          return item;
        });
      } else {
        const calc = this.api.getBlank();
        if (this.currentId === "new") {
          this._currentId = null;
        }
        if (this.action === "new-for-worker") {
          calc.worker.accountId = this.currentId;
        } else {
          calc.worker.employmentId = this.currentId;
        }
        this._currentId = null;
        this.setDefaultPaymentChannel(calc);
        return this.setStatus(this.calculationsApi.updateFromEmployment(calc, false, this.updateRows))
          .then((item) => {
            this.setCurrentRef(item);
            return item;
          });
      }
    } else {
      if (this.isNew() && this.sessionService.isInRole("worker")) {
        if (this.parentController) {
          return this.parentController.reload().then((item) => {
            this._currentId = null;
            return item;
          });
        } else {
          const getNewCalc = (): Promise<Calculation> => {
            if (this.action === "copy-as-new") {
              return this.setStatus(this.api.getSingle(this.currentId)).then((item) => {
                return this.copyItem(item || this.api.getBlank());
              });
            } else {
              return Promise.resolve(this.api.getBlank());
            }
          };
          return getNewCalc().then((newCalc) => {
            newCalc.worker.isSelf = true;
            newCalc.employer.isSelf = false;
            this._currentId = null;
            this.setDefaultPaymentChannel(newCalc);
            return this.setStatus(this.calculationsApi.recalculate(newCalc)).then((calc) => {
              this.setCurrentRef(calc);
              return calc;
            });
          });
        }
      }

      return super.reload().then((reloaded) => {
        if (super.isNew()) {
          this.setDefaultPaymentChannel(reloaded);
        }
        return reloaded;
      });
    }
  }

  /**
   * Creates a copy of a given item.
   * This is a synchronous method that should basically convert a saved item to a new item.
   * @param copySource Item (container item) to copy as new.
   */
  public copyItem(copySource: Calculation): Calculation {
    const copy = super.copyItem(copySource);
    copy.id = null;
    copy.info.workStartDate = null;
    copy.info.workEndDate = null;
    copy.info.workerMessage = null;
    copy.info.paymentId = null;
    copy.info.payrollId = null;
    copy.info.backofficeNotes = null;
    copy.info.pensionPaymentDate = null;
    copy.info.pensionPaymentRef = null;
    copy.info.pensionPaymentSpecifier = null;
    copy.info.paymentChannel = PaymentChannel.Undefined;
    copy.info.messageThreadId = null;
    copy.worktime = {};
    copy.workflow.status = CalculationStatus.Draft;
    copy.workflow.paidAt = null;
    copy.workflow.salaryDate = null;
    copy.workflow.requestedSalaryDate = null;
    copy.workflow.salaryPaidAt = null;
    copy.createdAt = null;
    copy.updatedAt = null;
    if (copy.framework) {
      copy.framework.numberOfDays = 0;
    }
    // HACK: remove payroll-service flag. This will be replaced with invoicing flags eventually.
    if (copy.usecase &&
      (copy.usecase.uri === "palkkaus.fi/usecases/payroll-service/pro-calc" ||
        copy.usecase.uri === "palkkaus.fi/usecases/payroll-service/calc")) {
      copy.usecase = {};
    }
    copy.isReadOnly = false;
    this.setDefaultPaymentChannel(copy);
    return copy;
  }

  /** Shows the paymentd dialog / the invoices UI. */
  public showPaymentDialog() {
    this.invoicesService.showPaymentDialog(this.current);
  }

  /** Shows inspection dialog for invoices of paid calculation*/
  public showInvoicesDialog() {
    this.invoicesService.showPalkkausPersonalInvoicesDialog(this.current);
  }

  /**
   * Updates the calculation from Employment ID by deleting all the rows and re-setting the employment.
   * @param employmentId Employement Id to set.
   * If null, the employment Id already in calculation is used:
   * This method cannot be used to remove the Worker (extend if necessary).
   * @param updateRows If true, updates also the calc rows from the default rows in
   * employment relation. If false, the rows are kept as is.
   * @returns The Calculation after recalc (no saving).
   */
  public updateFromEmployment(employmentId: string = null, updateRows: boolean): Promise<Calculation> {
    if (employmentId) {
      this.current.worker.employmentId = employmentId;
    }
    return this.calculationsApi.updateFromEmployment(this.current, false, updateRows).then((calc) => {
      super.setCurrentValue(calc);
      return calc;
    });
  }

  /**
   * Cancels the payment or invoice processing for the current calculation.
   */
  public cancel(): Promise<Calculation> {
    const eprWarningMessage = this.invoicesService.getEprWarningForCancellation(this.current.workflow?.salaryDate, this.current.workflow?.status == CalculationStatus.PaymentSucceeded);
    const eprText = eprWarningMessage ? "\n" + eprWarningMessage : "";
    return this.uiHelpers.showConfirm(Translations.get("SALAXY.NG1.CalcResultsComponent.cancelDialogTitle") + "?", Translations.get("SALAXY.NG1.CalcResultsComponent.cancelDialogDescr") + `${eprText}`, "SALAXY.NG1.CalcResultsComponent.cancelDialogYes", "SALAXY.NG1.CalcResultsComponent.cancelDialogNo").then((result) => {
      if (result) {
        const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
        return this.calculationsApi.cancel(this.currentId).then((item: Calculation) => {
          this.setCurrentValue(item);
          loading.dismiss();
          return this.current;
        });
      } else {
        return this.current;
      }
    });
  }

  /**
   * Opens the list for selecting a new paid calculation for repair.
   * @param onAfterAction Function to call after the action.
   * @param calcId Optional ID of the calculation to repair.
   */
  public launchCreateRepair(onAfterAction: () => Promise<void>, calcId: string = null) {
    if (calcId) {
      const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
      return this.calculationsApi.startRepair(calcId).then(() => {
        // Wait for the index
        return this.uiHelpers.wait().then(() => {
          loading.dismiss();
          if (onAfterAction) {
            return onAfterAction();
          }
        });
      });
    }
    else {
      return this.uiHelpers.openEditDialog(
        "salaxy-components/modals/calc/CalcList.html",
        [],
        {
          title: "SALAXY.UI_Terms.select",
          category: "paid",
          options: { $filter: this.sessionService.isInRole("admin") ? "status eq 'paymentSucceeded'" : "status eq 'paymentSucceeded' and not( data/paymentChannel eq 'palkkausCfaTest' or data/paymentChannel eq 'palkkausCfaPaytrail' or data/paymentChannel eq 'palkkausEcfaPaytrail' or data/paymentChannel eq 'palkkausCfaReference' or data/paymentChannel eq 'palkkausEcfaReference' or data/paymentChannel eq 'palkkausCfaFinvoice' or data/paymentChannel eq 'palkkausEcfaFinvoice' )" },
          okButtonText: "SALAXY.UI_Terms.ok",
          cancelButtonText: "SALAXY.UI_Terms.cancel",
        }).then((result) => {
          if (result.action === "ok" && result.item.length > 0) {
            const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
            return Promise.all(result.item.map(x => this.calculationsApi.startRepair(x.id))).then(() => {
              // Wait for the index
              return this.uiHelpers.wait().then(() => {
                loading.dismiss();
                if (onAfterAction) {
                  return onAfterAction();
                }
              });
            });
          } else {
            return;
          }
        });
    }
  }

  /**
   * If true, the form controls should be read-only (no control at all).
   */
  public get isReadOnly() {
    return super.isReadOnly || this.current.workflow?.status === CalculationStatus.PaymentStarted;
  }

  /**
   * Returns true if there is no net salary payment to worker at all.
   */
  public get isCurrentZeroPayment(): boolean {
    return CalculatorLogic.isZeroPayment(this.current);
  }

  private setDefaultPaymentChannel(calc: Calculation) {
    if (calc && calc.info && this.sessionService.getSession() && (!calc.info.paymentChannel || calc.info.paymentChannel === PaymentChannel.Undefined)) {
      calc.info.paymentChannel = this.invoicesService.defaultChannel;
    }
  }

  /**
   * HACK: This will be removed after moved to useCase based calculators.
   * Temporary redirecting to correct details view.
   * TODO: Should be replaced with full usecase logic based implementation.
   */
  public showDetails(item: Calculation | CalculationListItem) {
    // HACK: This implementation is a bit hacky, but it should work.
    // This is to be replaced with logic that checks the usecase and makes the redirect accordingly.
    if (!this.sessionService.isInRole(SystemRole.Test)) {
      if ((item as Calculation)?.workflow?.status == CalculationStatus.History) {
        this.detailsUrl = "/calc/history/";
      }
      if ((item as CalculationListItem)?.status == CalculationStatus.History) {
        this.detailsUrl = "/calc/history/";
      }
    }
    if ((item as CalculationListItem)?.status == CalculationStatus.Repair) {
      this.detailsUrl = "/calc/repair/";
    }
    return super.showDetails(item);
  }

  /** Shows the earnings payment report for the epr. */
  public showEpr(item: Calculation | CalculationListItem) {
    return this.calculationsApi.getEpr(item.id).then((epr) => {
      if (!epr) {
        this.uiHelpers.showAlert("Palkkatietoilmoitusta ei ole", "Palkkatietoilmoitusta ei ole vielä luotu tälle laskelmalle.");
        return null;
      }
      return this.$location.path("/irepr/details/" + epr.id);
    });
  }
}
